import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';

const DB_NAME = 'rebuildme';
const TABLE_NAME = 'kv_store';
const SQLITE_MIGRATION_FLAG = 'rebuildme_sqlite_migrated_v1';

let sqlite = null;
let db = null;
let initialized = false;
let sqliteReady = false;
let writeQueue = Promise.resolve();

const shouldMigrateKey = (key) => key && (key.startsWith('rebuildme_') || key === 'rebuildme_biometrics_enabled');

const enqueueWrite = (task) => {
  writeQueue = writeQueue
    .then(task)
    .catch((error) => {
      console.warn('SQLite write failed:', error);
    });
};

const ensureConnection = async () => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  if (db) {
    return true;
  }

  try {
    if (!sqlite) {
      sqlite = new SQLiteConnection(CapacitorSQLite);
    }

    await sqlite.checkConnectionsConsistency();
    const isConn = await sqlite.isConnection(DB_NAME, false);

    if (isConn.result) {
      db = await sqlite.retrieveConnection(DB_NAME, false);
    } else {
      db = await sqlite.createConnection(DB_NAME, false, 'no-encryption', 1, false);
    }

    await db.open();
    await db.execute(
      `CREATE TABLE IF NOT EXISTS ${TABLE_NAME} (
        key TEXT PRIMARY KEY NOT NULL,
        value TEXT,
        updated_at INTEGER
      );`
    );

    return true;
  } catch (error) {
    console.warn('SQLite initialization failed, falling back to localStorage cache:', error);
    db = null;
    return false;
  }
};

const hydrateLocalCacheFromSQLite = async () => {
  if (!db) return;

  const result = await db.query(`SELECT key, value FROM ${TABLE_NAME};`);
  const rows = result?.values || [];

  rows.forEach((row) => {
    if (row?.key && typeof row.value === 'string') {
      localStorage.setItem(row.key, row.value);
    }
  });
};

const migrateLocalStorageToSQLite = async () => {
  if (!db) return;

  const alreadyMigrated = localStorage.getItem(SQLITE_MIGRATION_FLAG) === 'true';
  const countResult = await db.query(`SELECT COUNT(*) as count FROM ${TABLE_NAME};`);
  const sqliteCount = Number(countResult?.values?.[0]?.count || 0);

  if (alreadyMigrated && sqliteCount > 0) {
    return;
  }

  const statements = [];
  const now = Date.now();

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!shouldMigrateKey(key)) continue;

    const value = localStorage.getItem(key);
    if (value === null) continue;

    statements.push({
      statement: `INSERT OR REPLACE INTO ${TABLE_NAME} (key, value, updated_at) VALUES (?, ?, ?);`,
      values: [key, value, now],
    });
  }

  if (statements.length > 0) {
    await db.executeSet(statements);
  }

  localStorage.setItem(SQLITE_MIGRATION_FLAG, 'true');
  await db.run(
    `INSERT OR REPLACE INTO ${TABLE_NAME} (key, value, updated_at) VALUES (?, ?, ?);`,
    [SQLITE_MIGRATION_FLAG, 'true', now]
  );
};

export const initializeSQLiteStorage = async () => {
  if (initialized) {
    return sqliteReady;
  }

  initialized = true;
  const connected = await ensureConnection();
  if (!connected) {
    sqliteReady = false;
    return false;
  }

  try {
    await hydrateLocalCacheFromSQLite();
    await migrateLocalStorageToSQLite();
    sqliteReady = true;
    return true;
  } catch (error) {
    console.warn('SQLite data sync failed, keeping localStorage cache active:', error);
    sqliteReady = false;
    return false;
  }
};

export const sqliteStorage = {
  getItem(key) {
    return localStorage.getItem(key);
  },

  setItem(key, value) {
    localStorage.setItem(key, value);

    if (!sqliteReady || !db || !shouldMigrateKey(key)) return;

    const stringValue = String(value);
    enqueueWrite(async () => {
      await db.run(
        `INSERT OR REPLACE INTO ${TABLE_NAME} (key, value, updated_at) VALUES (?, ?, ?);`,
        [key, stringValue, Date.now()]
      );
    });
  },

  removeItem(key) {
    localStorage.removeItem(key);

    if (!sqliteReady || !db || !shouldMigrateKey(key)) return;

    enqueueWrite(async () => {
      await db.run(`DELETE FROM ${TABLE_NAME} WHERE key = ?;`, [key]);
    });
  },

  clear() {
    localStorage.clear();

    if (!sqliteReady || !db) return;

    enqueueWrite(async () => {
      await db.execute(`DELETE FROM ${TABLE_NAME};`);
    });
  },
};
